<template>
  <nav class="source-filters-container" :aria-label="allSourcesLabel">
    <!-- Rad 1: Landfilter / Segmented tabs -->
    <div class="country-row">
      <div class="country-tabs">
        <button
          v-for="country in countries"
          :key="country.id"
          type="button"
          class="country-tab"
          :class="{ active: selectedCountry === country.id }"
          @click="$emit('select-country', country.id)"
        >
          <span class="country-flag">{{ country.flag }}</span>
          <span class="country-name">{{ getCountryName(country) }}</span>
          <span class="country-badge" v-if="countryCounts[country.id] !== undefined">
            {{ countryCounts[country.id] }}
          </span>
        </button>
      </div>
    </div>

    <!-- Rad 2: Källfilter sorterat efter antal nyheter -->
    <div class="filter-scroll">
      <button 
        type="button"
        class="filter-pill"
        :class="{ active: selectedSource === 'all' }"
        @click="$emit('select-source', 'all')"
      >
        <span class="pill-label">{{ allSourcesLabel }}</span>
        <span class="pill-count">{{ totalCount }}</span>
      </button>

      <button 
        v-for="source in sortedSources" 
        :key="source.id"
        type="button"
        class="filter-pill"
        :class="{ active: selectedSource === source.id }"
        @click="$emit('select-source', source.id)"
      >
        <span 
          class="source-dot" 
          :style="{ backgroundColor: source.badgeColor }"
          aria-hidden="true"
        ></span>
        <span class="pill-label">{{ source.name }}</span>
        <span class="pill-count" v-if="counts[source.id] !== undefined">
          {{ counts[source.id] }}
        </span>
      </button>
    </div>
  </nav>
</template>

<script>
import { t } from '../services/i18n'

export default {
  name: 'SourceFilters',
  props: {
    lang: {
      type: String,
      default: 'en'
    },
    countries: {
      type: Array,
      required: true
    },
    selectedCountry: {
      type: String,
      default: 'all'
    },
    countryCounts: {
      type: Object,
      default: () => ({})
    },
    sources: {
      type: Array,
      required: true
    },
    selectedSource: {
      type: String,
      default: 'all'
    },
    counts: {
      type: Object,
      default: () => ({})
    },
    totalCount: {
      type: Number,
      default: 0
    }
  },
  emits: ['select-source', 'select-country'],
  computed: {
    allSourcesLabel() {
      return t(this.lang, 'allSources')
    },
    sortedSources() {
      // Filtrera först baserat på valt land
      let filtered = this.sources
      if (this.selectedCountry !== 'all') {
        filtered = this.sources.filter(s => s.country === this.selectedCountry)
      }

      // Rangordna baserat på antalet nyheter i flödet (fallande ordning)
      return [...filtered].sort((a, b) => {
        const countA = this.counts[a.id] || 0
        const countB = this.counts[b.id] || 0
        if (countB !== countA) {
          return countB - countA // flest nyheter först
        }
        return a.name.localeCompare(b.name)
      })
    }
  },
  methods: {
    getCountryName(country) {
      if (this.lang === 'sv') {
        return country.nameSv || country.name || country.id
      } else if (this.lang === 'de') {
        return country.nameDe || country.name || country.id
      }
      return country.nameEn || country.name || country.id
    }
  }
}
</script>

<style scoped>
.source-filters-container {
  background-color: transparent;
  border-bottom: 1px solid var(--border-color);
}

/* Rad 1: Landsflikar */
.country-row {
  max-width: 900px;
  margin: 0 auto;
  padding: 8px 16px 4px;
}

.country-tabs {
  display: inline-flex;
  background-color: var(--pill-bg);
  padding: 3px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  gap: 2px;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}

.country-tabs::-webkit-scrollbar {
  display: none;
}

.country-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.country-tab:hover {
  color: var(--text-primary);
}

.country-tab.active {
  background-color: var(--card-bg);
  color: var(--text-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.country-flag {
  font-size: 0.95rem;
}

.country-badge {
  font-size: 0.72rem;
  font-weight: 700;
  background-color: var(--badge-count-bg);
  padding: 1px 6px;
  border-radius: 10px;
  color: var(--text-muted);
}

.country-tab.active .country-badge {
  color: var(--text-primary);
}

/* Rad 2: Käll-pills */
.filter-scroll {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px 10px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 900px;
  margin: 0 auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.filter-scroll::-webkit-scrollbar {
  display: none;
}

.filter-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: var(--pill-bg);
  color: var(--pill-text);
  border: 1px solid var(--pill-border);
  padding: 5px 11px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.filter-pill:hover {
  background-color: var(--pill-hover-bg);
  border-color: var(--pill-hover-border);
}

.filter-pill.active {
  background-color: var(--pill-active-bg);
  color: var(--pill-active-text);
  border-color: var(--pill-active-bg);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.source-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.pill-count {
  font-size: 0.74rem;
  opacity: 0.7;
  font-weight: 500;
  margin-left: 2px;
}

.filter-pill.active .pill-count {
  opacity: 0.9;
}

@media (max-width: 600px) {
  .country-tabs {
    width: 100%;
    display: flex;
    overflow-x: auto;
  }
  .country-tab {
    flex-shrink: 0;
    padding: 5px 8px;
    font-size: 0.78rem;
  }
}
</style>
